import React, {
	memo,
	useMemo,
	useState,
	useCallback,
	useContext,
	useLayoutEffect,
} from 'react';
import {
	LoggerContext,
	PreviewContext,
	ScriptTagContext,
	useVisitTrigger,
	TrackError,
	useSendApiLogs,
	isRATAvailable,
} from '@rmc/webcx-web-libs';
import { useEventsApiPostEvent } from '../../api/v3/web/events/post';
import { CampaignsContext } from '../../context/CampaignsContext';
import { ClickTypeEnum } from '../../types/clickType.d';
import { EventType } from '../../types/EventType';
import { useGetValidCampaigns } from '../../hooks/useGetValidCampaigns';

import { TrackAppDisplay } from '../../lib/RAT';
import { Campaign } from '../Campaign';
import { PopupBackground } from '../PopupBackground';
import { getMainAppInfo, getAction } from './utils';
import {
	VERSION,
	RAT_PROJECT_KEY,
	REDIRECT_HOST,
	SCREEN,
	API_KEY_EVENT_LOGGER,
	API_URL_EVENT_LOGGER,
} from '../../config';
import { Script } from '../Script';

const App = function App() {
	const [isBackground, setIsBackground] = useState(false);
	const scriptData = useContext(ScriptTagContext);
	const previewQuery: { rcxpv?: string } = useContext(PreviewContext);
	const { updateGlobalLog } = useContext(LoggerContext);
	const {
		campaignIdCorrelationInfo,
		isLoading: cdnLoading,
		error: campaignIdsError,
	} = useContext(CampaignsContext);

	const {
		clientId,
		ratAccountId,
		ratServiceType,
		ratServiceId,
		backupSdkSrc,
		ratApiAccountSrc,
	} = scriptData;

	const { sessionId, domainScopedSessionId, device, userId, isMobile } =
		getMainAppInfo();

	const isPreview = Object.keys(previewQuery).length !== 0;
	const pageUrl = window?.location?.href || '';
	const campaignIds = useMemo(
		() => campaignIdCorrelationInfo.map((info) => info.campaignId),
		[campaignIdCorrelationInfo]
	);

	const campaignsPayload = useVisitTrigger({
		cdnCampaignIds: campaignIds,
		isLoading: cdnLoading,
		isVisTrigDisabled: isPreview,
	});

	const {
		campaigns: campaignList,
		initialCampaigns,
		invalidCampaigns,
		error,
		isLoading,
	} = useGetValidCampaigns({
		device,
		userId,
		sessionId,
		domainScopedSessionId,
		previewQuery,
		onLog: updateGlobalLog,
		campaigns: campaignsPayload,
		clientId,
	});

	const campaignsWithCorrelationId = useMemo(
		() =>
			campaignList.map((campaign) => {
				const correlationId = campaignIdCorrelationInfo.find(
					(info) => info.campaignId === campaign.campaignId
				)?.correlationId;

				return {
					...campaign,
					correlationId,
				};
			}),
		[campaignList, campaignIdCorrelationInfo]
	);

	const campaign = useMemo(
		() => campaignsWithCorrelationId?.[0],
		[campaignsWithCorrelationId]
	);

	useLayoutEffect(() => {
		if (
			!isRATAvailable() &&
			!ratApiAccountSrc &&
			!window?.location?.href.includes('localhost')
		) {
			TrackError({
				action: 'ERROR',
				error: 'RAT_TRACKING_UNAVAILABLE',
				version: VERSION,
				projectKey: RAT_PROJECT_KEY,
				screen: 'TRACKING',
				isMobile,
				userId,
				sessionId,
				clientId,
				pageUrl,
				eventLoggerUrl: API_URL_EVENT_LOGGER,
				eventLoggerApiKey: API_KEY_EVENT_LOGGER,
			});
		} else if (window?.location?.href.includes('localhost')) {
			/* eslint-disable no-console */
			console.log('RAT_TRACKING_UNAVAILABLE', {
				action: 'ERROR',
				error: 'RAT_TRACKING_UNAVAILABLE',
				version: VERSION,
				projectKey: RAT_PROJECT_KEY,
				screen: 'TRACKING',
				isMobile,
				userId,
				sessionId,
				clientId,
				pageUrl,
			});
		}
	}, []);

	const postApiEvent = useEventsApiPostEvent<EventType>({
		clientId,
		sessionId,
		userId,
		domainScopedSessionId,
		previewQuery,
		onError: (
			err: unknown,
			variables: {
				eventType: EventType;
				campaignId: string;
				contentId: string;
				url?: string;
				type?: ClickTypeEnum;
			}
		) => {
			const { eventType, contentId, campaignId, type } = variables;

			// NOTE: correlationId is tracked for debugging purposes
			// It's added from top level context since it's not present in the API
			const correlationId = campaignIdCorrelationInfo.find(
				(info) => info.campaignId === campaignId
			)?.correlationId;

			TrackError({
				action: `${getAction(eventType, type)}_ERROR`,
				error: err,
				version: VERSION,
				projectKey: RAT_PROJECT_KEY,
				screen: 'APP',
				campaignId,
				correlationId:
					typeof correlationId === 'string' ? correlationId : undefined,
				contentId,
				isMobile,
				sessionId,
				domainScopedSessionId,
				userId,
				clientId,
				pageUrl,
				eventLoggerUrl: API_URL_EVENT_LOGGER,
				eventLoggerApiKey: API_KEY_EVENT_LOGGER,
				isPreview,
			});
		},
	});

	useSendApiLogs({
		screen: 'MESSAGE_API',
		version: VERSION,
		projectKey: RAT_PROJECT_KEY,
		apiError: error,
		apiIsLoading: isLoading,
		apiItems: initialCampaigns,
		apiItemsInvalid: invalidCampaigns,
		isMobile,
		sessionId,
		domainScopedSessionId,
		userId,
		clientId,
		pageUrl,
		eventLoggerUrl: API_URL_EVENT_LOGGER,
		eventLoggerApiKey: API_KEY_EVENT_LOGGER,
		isPreview,
	});

	const postCloseEvent = () =>
		postApiEvent({
			eventType: EventType.CLOSE,
			campaignId: campaign?.campaignId,
			contentId: campaign?.contentId,
		});

	const postViewEvent = () =>
		postApiEvent({
			eventType: EventType.VIEW,
			campaignId: campaign?.campaignId,
			contentId: campaign?.contentId,
		});

	const onDisplay = useCallback(() => {
		setIsBackground(true);
		return TrackAppDisplay({
			version: VERSION,
			projectKey: RAT_PROJECT_KEY,
			campaignId: campaign?.campaignId,
			contentId: campaign?.contentId,
			correlationId: campaign?.correlationId,
			ratAccountId,
			ratServiceType,
			ratServiceId,
			isMobile,
			sessionId,
			domainScopedSessionId,
			userId,
			clientId,
			ratApiAccountSrc,
			isPreview,
			trackCb: () => postViewEvent(),
		});
	}, [
		campaign?.campaignId,
		campaign?.contentId,
		campaign?.correlationId,
		ratAccountId,
		ratServiceType,
		ratServiceId,
		isMobile,
		postViewEvent,
	]);

	const onClose = useCallback(() => {
		setIsBackground(false);
		postCloseEvent();
	}, [
		campaign?.campaignId,
		campaign?.contentId,
		campaign?.correlationId,
		ratAccountId,
		ratServiceType,
		ratServiceId,
		isMobile,
		postCloseEvent,
	]);

	if ((campaignIdsError || error) && backupSdkSrc) {
		const body = document.body || document.getElementsByTagName('body')[0];
		return <Script url={backupSdkSrc} container={body} />;
	}

	if (isLoading || !campaign) {
		return null;
	}

	return (
		<>
			<Campaign
				{...campaign}
				clickLinkData={{
					redirectHost: REDIRECT_HOST,
					version: VERSION,
					sessionId,
					clientId,
					issueId: campaign.correlationId || null,
					screen: SCREEN,
					...(previewQuery?.rcxpv ? { rcxpv: previewQuery?.rcxpv } : {}),
				}}
				onClickLink={() => {
					setIsBackground(false);
				}}
				onDisplay={onDisplay}
				onClose={onClose}
			/>

			{campaign.payload?.style?.background && (
				<PopupBackground
					background={campaign.payload.style.background}
					isBackground={isBackground}
				/>
			)}
		</>
	);
};

export const MainApp = memo(App);
